"use client";

import { Box, Typography } from "@mui/material";
import SectionContainer from "@/components/marketing/SectionContainer";
import CTAButton from "@/components/marketing/CTAButton";
import { getPortalUrl } from "@/lib/portal";

/**
 * CTA Section Component
 *
 * Usage:
 * - page prop: 'homepage', 'about', 'academics', 'contact', 'portal' (data-driven copy)
 * - variant="questions": Admissions page CTA (different layout)
 *
 * Copy defined in /docs/global-cta.md
 */

const CTA_CONTENT = {
  homepage: {
    title: "Ready to Join the Hive?",
    body: "We are looking for families who want more from education. Explore the benefits of becoming a founding family or start your application today.",
    primaryButton: {
      text: "Start Your Application",
      link: "/portal",
    },
    secondaryButton: {
      text: "Founding Family Perks",
      link: "/admissions",
    },
  },
  about: {
    title: "Be Part of the Movement",
    body: "You've seen our vision for a better future. Now see how we put it into practice and what it means for your child's education.",
    primaryButton: {
      text: "Start Your Application",
      link: "/portal",
    },
    secondaryButton: {
      text: "View Admissions Process",
      link: "/admissions",
    },
  },
  academics: {
    title: "Experience a New Way of Learning",
    body: "Our curriculum is designed to spark curiosity. If you believe your child deserves to love school, we invite you to join us.",
    primaryButton: {
      text: "Start Your Application",
      link: "/portal",
    },
    secondaryButton: {
      text: "Meet the Founder",
      link: "/about",
    },
  },
  contact: {
    title: "Ready to Get Started?",
    body: "If you have all the answers you need, you can skip the conversation and begin your enrollment process directly.",
    primaryButton: {
      text: "Start Your Application",
      link: "/portal",
    },
    secondaryButton: {
      text: "Learn About Sun Hive",
      link: "/about",
    },
  },
  portal: {
    title: "Ready to get started?",
    body: "Access your dashboard to track applications, manage enrollment, or view resources.",
    primaryButton: {
      text: "Launch Honeycomb Portal",
      link: "PORTAL_URL",
    },
    secondaryButton: {
      text: "Need Help?",
      link: "/contact",
    },
  },
};

export default function CTASection({ page = "homepage", variant }) {
  // Alternative CTA for Admissions page
  if (variant === "questions") {
    return (
      <SectionContainer
        py={{ xs: 6, md: 8 }}
        backgroundColor="background.default"
      >
        <Box
          sx={{
            textAlign: "center",
            maxWidth: "500px",
            mx: "auto",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: "1.5rem", md: "1.75rem" },
              color: "text.primary",
              mb: 2,
              fontWeight: 600,
            }}
          >
            Have Questions?
          </Typography>
          <Typography sx={{ color: "text.secondary", mb: 3, lineHeight: 1.7 }}>
            We&apos;re here to help. Reach out and we&apos;ll be happy to chat.
          </Typography>
          <CTAButton href="/contact" variant="secondary">
            Get in Touch
          </CTAButton>
        </Box>
      </SectionContainer>
    );
  }

  // Data-driven standard CTA
  const content = CTA_CONTENT[page] || CTA_CONTENT.homepage;
  const portalUrl = getPortalUrl();
  const primaryHref =
    content.primaryButton.link === "PORTAL_URL"
      ? portalUrl
      : content.primaryButton.link;

  return (
    <SectionContainer
      py={{ xs: 8, md: 10 }}
      backgroundColor="background.default"
    >
      <Box
        sx={{
          textAlign: "center",
          maxWidth: "700px",
          mx: "auto",
          p: { xs: 4, md: 6 },
          backgroundColor: "background.section",
          borderRadius: "24px",
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: "2rem", md: "2.5rem" },
            color: "text.primary",
            mb: 2,
            fontWeight: 600,
          }}
        >
          {content.title}
        </Typography>
        <Typography sx={{ color: "text.secondary", mb: 4, lineHeight: 1.7 }}>
          {content.body}
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <CTAButton
            href={primaryHref}
            variant="primary"
            target={primaryHref === portalUrl ? "_blank" : undefined}
          >
            {content.primaryButton.text}
          </CTAButton>
          <CTAButton href={content.secondaryButton.link} variant="secondary">
            {content.secondaryButton.text}
          </CTAButton>
        </Box>
      </Box>
    </SectionContainer>
  );
}
